import {
  CoinName,
  ExchangeName,
  GroupData,
  MarketplaceSubGroup,
  UserBasicDetails,
} from "@shared/types";
import { CSSProperties } from "react";

export enum OnboardingStep {
  CONNECT_EXCHANGE,
  TRADING_METHOD,
  METHOD_SETTINGS,
  OVERVIEW,
}

export enum OnboardingDetailedStep {
  VERIFY_EMAIL = "verify email",
  VALIDATE_TELEGRAM_ACCOUNT = "validate telegram account",
  SELECT_EXCHANGE = "select exchange",
  CONNECT_EXCHANGE = "connect exchange",
  TRADING_METHOD = "trading method",
  COPY_DCA_BOT = "copy dca bot",
  FOLLOW_SIGNALS_GROUP = "follow signals group",
  CONNECT_EXTERNAL_ACCOUNT = "connect external account",
  SIGNALS_GROUP_OVERVIEW = "signals group overview",
  DCA_BOT_OVERVIEW = "dca bot overview",
  SKIP = "skip",
}

export enum ExtraInfoType {
  CONNECT_API = "connect api",
  NO_EXCHANGE_ACCOUNT = "no exchange account",
  API_KEYS = "api keys",
}

export enum TradingMethodType {
  SIGNALS_GROUP = "signals group",
  DCA_BOT = "DCA bot",
}

export enum SignalsGroupsType {
  MY_GROUPS = "tabs.my_groups",
  FREE_TRIAL = "tabs.free_trial",
  FREE = "tabs.free",
  PERSONAL = "tabs.personal",
}

export type OnboardingSignalsGroupsType = Exclude<
  SignalsGroupsType,
  SignalsGroupsType.PERSONAL
>;

export enum DcaBotsType {
  TRENDING = "Trending",
  LONG_RUNNING = "Long Running",
  HIGH_VOLUME = "High Volume",
}

export enum ExternalAccountType {
  TELEGRAM = "Telegram",
  DISCORD = "Discord",
}

export interface GetBasicDetailsFromUuidData {
  data: UserBasicDetails | undefined;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  showUuidInUseMessage: () => void;
  isInvalidUuid: boolean;
  refetch: () => void;
  getUserStatus: any;
  isAlreadyRegisteredToAppError: boolean;
}

export type AccountData = {
  id: number;
  exchange?: ExchangeName;
  total?: number;
  available?: number;
  signals: number;
  accountName?: string;
};

export type CoinData = {
  coin: CoinName;
  numOfSignals?: number;
  signalsPercentage?: number;
  total: number;
  available: number;
};

export type GroupType = (GroupData | MarketplaceSubGroup) & {
  id: number;
  group_type: SignalsGroupsType;
};

export interface GroupsSectionData {
  groupType: SignalsGroupsType;
  groups: GroupType[] | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  shouldHide?: boolean;
}

export interface SignalsGroupsSectionProps {
  groupType: SignalsGroupsType;
  groups?: GroupType[];
  isLoading?: boolean;
  isFetching?: boolean;
  isSuccess?: boolean;
  titleStyle?: CSSProperties;
  extraButton?: React.JSX.Element;
  showSearch?: boolean;
}

export enum ExtraInfo {
  WHY_NEED = "why need",
  UUID = "uuid",
}

export interface ResendEmailProps {
  isVerify?: boolean;
  addBorder?: boolean;
}

export interface MethodCardProps {
  tradingMethod: TradingMethodType;
  onClick: () => void;
  tooltipDescription?: React.JSX.Element;
  selected: boolean;
}

export interface AccountsListProps {
  preferredExchange?: string | null;
  groupData?: GroupData;
  symbol?: string;
  isBot?: boolean;
  isOnboarding?: boolean;
}

export interface AccountRowProps {
  currency?: string;
  account: AccountData;
  disabled: boolean;
  total?: number;
  available?: number;
  isBot?: boolean;
  totalSignals?: number;
  spans: number[];
  isOnboarding?: boolean;
}

export interface AccountMainCoinsProps {
  account: AccountData;
  spans: number[];
  isRadio?: boolean;
}

export interface MainCoinProps {
  coinData: CoinData;
  spans: number[];
  isRadio?: boolean;
}

export interface ConfirmAutoTradingModalProps {
  isVisible: boolean;
  content?: string[];
  onOk: () => void;
  onCancel: () => void;
  buttonLoading?: boolean;
  centered?: boolean;
}

export interface ConfirmBulkEditModalProps
  extends ConfirmAutoTradingModalProps {
  title: string;
  numValidBots: number;
  numInvalidBots: number;
}

export interface QuickConnectWarningMessageProps {
  warningMessage: React.JSX.Element | string;
  stepOne: React.JSX.Element | string;
  stepTwo: React.JSX.Element | string;
  stepThree: React.JSX.Element | string;
  onTryAgain: () => void;
  tryAgainLoading: boolean;
  secondaryButtonText: string;
  secondaryAction: () => void;
  isOnboarding?: boolean;
  showWarning?: boolean;
}

export interface SharedSkipOnboardingModalProps {
  isVisible: boolean;
  content?: string;
  onCancel: () => void;
  newPath: string;
}

export interface SharedTelegramUserPreviewProps {
  telegramUser?: UserBasicDetails;
  isLoading?: boolean;
  horizontal?: boolean;
  showRemoveUuidButton?: boolean;
  showInfoButton?: boolean;
  showTelegramIcon?: boolean;
  removeUuid?: () => void;
  showingContinueWithoutTelegram?: boolean;
}

export interface CoinGroupSignalsData {
  total: number;
  available: number;
  coin: string;
  numOfSignals: number | undefined;
  signalsPercentage: number | undefined;
  selectedAmount?: number;
  selectedPercent?: number;
}

export interface SendCodeButtonProps {
  onSendAuthCodeClick: () => void;
  showCodeSent?: boolean;
  showResendCode?: boolean;
  codeSentTooltipText?: string;
}

export type UrlQuickConnectQueryData = {
  statusCode: string | null;
  failMessage: string | null;
  accountBaseName: string | null;
  exchangesList?: string | null;
  exchange?: string | null;
};

export type CreatedAccountData = { exchange: string; client_name: string };
